import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Users, Building2, Plus, X, Mail, Phone, Globe, Trash2, Edit3,
  Search, Loader2, Tag, UserPlus,
} from "lucide-react";

export default function ContactsPage() {
  const [tab, setTab] = useState("contacts"); // contacts | organizations
  const [contacts, setContacts] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = q ? `?q=${encodeURIComponent(q)}` : "";
      const [c, o] = await Promise.all([
        api.get(`/contacts${params}`),
        api.get(`/organizations${params}`),
      ]);
      setContacts(c.data || []);
      setOrgs(o.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const onSearch = (e) => { if (e.key === "Enter") load(); };

  return (
    <div data-testid="contacts-page">
      <PageHeader
        eyebrow="Le Réseau"
        title="Contacts & Organizations"
        description="One place for every counterparty, lawyer, mediator, judge or ombudsman you work with. Linked to agreements, cases and signatures."
        action={
          <Button
            onClick={() => setAdding(true)}
            className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs"
            data-testid="new-contact-btn"
          >
            <UserPlus size={14} className="mr-2" /> New {tab === "contacts" ? "contact" : "organization"}
          </Button>
        }
      />

      <div className="flex items-center gap-6 border-b border-[#E8E3F0] mb-6">
        <button
          onClick={() => setTab("contacts")}
          className={`pb-3 text-sm border-b-2 ${tab === "contacts" ? "border-[#2A0F5A]" : "border-transparent text-[#4A5568] hover:text-[#2A0F5A]"}`}
          data-testid="tab-contacts"
        >
          <Users size={14} className="inline mr-1.5" /> Contacts ({contacts.length})
        </button>
        <button
          onClick={() => setTab("organizations")}
          className={`pb-3 text-sm border-b-2 ${tab === "organizations" ? "border-[#2A0F5A]" : "border-transparent text-[#4A5568] hover:text-[#2A0F5A]"}`}
          data-testid="tab-organizations"
        >
          <Building2 size={14} className="inline mr-1.5" /> Organizations ({orgs.length})
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 mb-5">
        <div className="flex-1 flex items-center border border-[#E8E3F0] bg-white px-3">
          <Search size={14} className="text-[#4A5568]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onSearch}
            placeholder="Search by name, email, role, tag…"
            className="flex-1 px-3 py-2 text-sm bg-transparent focus:outline-none"
            data-testid="contacts-search-input"
          />
          {q && <button onClick={() => { setQ(""); setTimeout(load, 0); }}><X size={13} className="text-[#4A5568]" /></button>}
        </div>
        <Button onClick={load} variant="outline" className="rounded-none border-[#E8E3F0] text-xs uppercase tracking-widest">Search</Button>
      </div>

      {loading ? (
        <div className="text-sm text-[#4A5568] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : tab === "contacts" ? (
        contacts.length === 0 ? (
          <EmptyState icon={Users} title="No contacts yet" description="Add the people you work with — counterparties, lawyers, mediators. Link them to organizations and reference them from agreements and envelopes."
            action={<Button onClick={() => setAdding(true)} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs"><UserPlus size={14} className="mr-2" /> New contact</Button>}
          />
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {contacts.map((c) => (
              <div key={c.id} className="border border-[#E8E3F0] bg-white p-4 group" data-testid={`contact-row-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg leading-tight">{c.name}</div>
                    {c.role && <div className="text-xs text-[#4A5568]">{c.role}</div>}
                    {c.organization_name && <div className="text-xs text-[#C5A059] mt-0.5">{c.organization_name}</div>}
                    <div className="mt-2 space-y-0.5 text-xs text-[#4A5568]">
                      {c.email && <div className="flex items-center gap-1.5"><Mail size={10} /> {c.email}</div>}
                      {c.phone && <div className="flex items-center gap-1.5"><Phone size={10} /> {c.phone}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
                    <button onClick={() => setEditing({ kind: "contact", data: c })} className="text-[#4A5568] hover:text-[#2A0F5A]" data-testid={`edit-contact-${c.id}`}><Edit3 size={13} /></button>
                    <button onClick={async () => {
                      if (!window.confirm(`Delete ${c.name}?`)) return;
                      await api.delete(`/contacts/${c.id}`); load(); toast.success("Deleted");
                    }} className="text-[#4A5568] hover:text-[#8a2121]"><Trash2 size={13} /></button>
                  </div>
                </div>
                {(c.tags || []).length > 0 && (
                  <div className="mt-3 pt-2 border-t border-[#E8E3F0] flex flex-wrap gap-1">
                    {c.tags.map((t) => <span key={t} className="text-[10px] uppercase tracking-wider border border-[#E8E3F0] px-1.5 py-0.5">{t}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : orgs.length === 0 ? (
        <EmptyState icon={Building2} title="No organizations yet" description="Keep every company, court, regulator, mediator or law firm centrally so contacts can link to them."
          action={<Button onClick={() => setAdding(true)} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs"><Plus size={14} className="mr-2" /> New organization</Button>}
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {orgs.map((o) => (
            <div key={o.id} className="border border-[#E8E3F0] bg-white p-4 group" data-testid={`org-row-${o.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="label-eyebrow mb-1">{o.type}</div>
                  <div className="font-serif text-lg leading-tight">{o.name}</div>
                  <div className="mt-2 space-y-0.5 text-xs text-[#4A5568]">
                    {o.email && <div className="flex items-center gap-1.5"><Mail size={10} /> {o.email}</div>}
                    {o.phone && <div className="flex items-center gap-1.5"><Phone size={10} /> {o.phone}</div>}
                    {o.website && <div className="flex items-center gap-1.5"><Globe size={10} /> {o.website}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => setEditing({ kind: "organization", data: o })} className="text-[#4A5568] hover:text-[#2A0F5A]"><Edit3 size={13} /></button>
                  <button onClick={async () => {
                    if (!window.confirm(`Delete ${o.name}?`)) return;
                    await api.delete(`/organizations/${o.id}`); load(); toast.success("Deleted");
                  }} className="text-[#4A5568] hover:text-[#8a2121]"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(adding || editing) && (
        <EntityModal
          kind={editing?.kind || tab.slice(0, -1)}
          initial={editing?.data}
          orgs={orgs}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EntityModal({ kind, initial, orgs, onClose, onSaved }) {
  const isOrg = kind === "organization";
  const [form, setForm] = useState(initial || (isOrg
    ? { name: "", type: "company", address: "", phone: "", email: "", website: "", notes: "", tags: [] }
    : { name: "", email: "", phone: "", address: "", role: "", organization_id: "", notes: "", tags: [] }
  ));
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || form.tags.includes(t)) return;
    setForm({ ...form, tags: [...form.tags, t] });
    setTagInput("");
  };
  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter((x) => x !== t) });

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const path = isOrg ? "/organizations" : "/contacts";
      const payload = { ...form };
      if (!isOrg && !payload.organization_id) payload.organization_id = null;
      if (!payload.email) delete payload.email;
      if (initial?.id) {
        await api.patch(`${path}/${initial.id}`, payload);
      } else {
        await api.post(path, payload);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-[#0B132B]/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="entity-modal">
      <div className="bg-white max-w-xl w-full mt-12 mb-12 border border-[#E8E3F0]">
        <div className="px-6 py-4 border-b border-[#E8E3F0] flex items-center justify-between">
          <h2 className="font-serif text-2xl">{initial ? "Edit" : "New"} {isOrg ? "organization" : "contact"}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={isOrg ? "Organization name" : "Full name"} className="rounded-none border-[#E8E3F0]" data-testid="entity-name-input" />
          {isOrg ? (
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border border-[#E8E3F0] px-3 py-2 text-sm bg-white">
              {["company", "court", "regulator", "mediator", "law_firm", "other"].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <>
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Role (e.g. Legal counsel)" className="rounded-none border-[#E8E3F0]" />
              {orgs.length > 0 && (
                <select value={form.organization_id || ""} onChange={(e) => setForm({ ...form, organization_id: e.target.value })} className="w-full border border-[#E8E3F0] px-3 py-2 text-sm bg-white" data-testid="entity-org-select">
                  <option value="">— No organization —</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              )}
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="rounded-none border-[#E8E3F0]" data-testid="entity-email-input" />
            <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="rounded-none border-[#E8E3F0]" />
          </div>
          {isOrg && <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website" className="rounded-none border-[#E8E3F0]" />}
          <Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" rows={2} className="rounded-none border-[#E8E3F0]" />
          <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" rows={2} className="rounded-none border-[#E8E3F0]" />

          <div>
            <div className="label-eyebrow mb-1.5 flex items-center gap-1"><Tag size={10} /> Tags</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.tags.map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-wider border border-[#E8E3F0] px-1.5 py-0.5 flex items-center gap-1">
                  {t} <button onClick={() => removeTag(t)}><X size={10} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} placeholder="Add tag…" className="rounded-none border-[#E8E3F0]" />
              <Button onClick={addTag} variant="outline" className="rounded-none border-[#E8E3F0] text-xs uppercase tracking-widest">Add</Button>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E8E3F0] flex justify-end gap-3 bg-[#FAFAF8]">
          <Button variant="outline" onClick={onClose} className="rounded-none border-[#E8E3F0]">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="entity-save-btn">
            {busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
